import React, { Component } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Storage as StorageIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { withStyles, WithStyles } from '@mui/styles';
import type { Theme } from '@mui/material/styles';

interface Tool {
  name: string;
  description?: string;
}

interface Capabilities {
  provider?: string;
  capabilities?: string[];
  tools?: Tool[];
}

interface Props extends WithStyles<typeof styles> {
  capabilities?: Capabilities | null;
}

const styles = (theme: Theme) => ({
  root: {
    marginTop: theme.spacing(2),
  },
  capabilityChip: {
    margin: theme.spacing(0.5),
  },
  toolItem: {
    paddingLeft: theme.spacing(3),
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
    verticalAlign: 'middle' as const,
  },
});

class ChatCapabilities extends Component<Props> {
  override render(): React.JSX.Element | null {
    const { classes, capabilities } = this.props;

    if (!capabilities) return null;

    return (
      <Box className={classes.root}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              <CodeIcon className={classes.sectionIcon} />
              Agent Capabilities
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box width="100%">
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Provider: {capabilities.provider} | Available Tools:{' '}
                {capabilities.tools?.length ?? 0}
              </Typography>

              <Divider style={{ margin: '16px 0' }} />

              <Typography variant="subtitle2" gutterBottom>
                <SearchIcon className={classes.sectionIcon} />
                What I Can Do
              </Typography>
              <Box marginBottom={2}>
                {capabilities.capabilities?.map((capability, index) => (
                  <Chip
                    key={index}
                    label={capability}
                    size="small"
                    variant="outlined"
                    className={classes.capabilityChip}
                  />
                ))}
              </Box>

              {capabilities.tools && capabilities.tools.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    <StorageIcon className={classes.sectionIcon} />
                    Available Search Tools
                  </Typography>
                  <List dense>
                    {capabilities.tools.map((tool, index) => (
                      <ListItem key={index} className={classes.toolItem}>
                        <ListItemText
                          primary={tool.name.replace(/_/g, ' ').toUpperCase()}
                          secondary={tool.description}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              <Divider style={{ margin: '16px 0' }} />

              <Typography variant="body2" color="textSecondary">
                💡 <strong>Tips:</strong> Try asking questions like "Find all stashers named
                John", "Show me stashcards for restaurants", or "Get summary statistics"
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  }
}

export default withStyles(styles)(ChatCapabilities);
